/**
 * @description Rising Games 指标阈值配置
 *
 * 用于计算 Rising Games 筛选的动态阈值，根据时间跨度自动调整
 *
 * 计算公式: threshold = dateDiff * baseValue
 * - daily 粒度: dateDiff 为天数
 * - monthly 粒度: dateDiff 为月数
 */

/**
 * 日期差值单位
 */
export type TDateUnit = 'days' | 'months';

/**
 * 指标阈值配置项
 */
export interface IMetricThresholdConfig {
  /** 日期差值单位 */
  unit: TDateUnit;
  /** 每单位时间的基础阈值 */
  baseValue: number;
}

/**
 * 指标过滤条件
 */
export interface IMetricFilter {
  /** 指标名称 */
  metric: string;
  /** 指标值条件 */
  metricValue: {
    /** 操作符 */
    opt: '>' | '>=' | '<' | '<=';
    /** 阈值 */
    value: number;
  };
}

/**
 * 筛选器参数类型
 */
export interface IFilterParams {
  /** 时间粒度 */
  granularity?: IGranularity;
  /** 开始日期 */
  start_date?: string;
  /** 结束日期 */
  end_date?: string;
  /** 其他筛选条件 */
  [key: string]: any;
}

/**
 * 指标阈值计算结果
 */
export interface IThresholdResult {
  /** 最小阈值 */
  min: number | null;
  /** 最大阈值 */
  max: number | null;
}

/**
 * Rising Games 指标阈值配置
 *
 * 配置说明:
 * - DAU: 日活用户数，每日50万基准
 * - Download: 下载量，每日5万基准
 * - Revenue: 收入，每日1万基准
 * - MAU: 月活用户数，每月100万基准
 *
 * @example
 * // 3天的 DAU 阈值 = 3 * 500,000 = 1,500,000
 * // 2月的 Download 阈值 = 2 * 500,000 = 1,000,000
 */
export const METRIC_THRESHOLD_CONFIG: IRecord<IMetricThresholdConfig> = {
  // ========== Daily 粒度 ==========
  /** DAU: 每日 50万 */
  daily__sensortower__dau: {
    baseValue: 500_000,
    unit: 'days',
  },
  /** Download: 每日 5万 */
  daily__sensortower__download: {
    baseValue: 50_000,
    unit: 'days',
  },
  /** Revenue: 每日 1万 */
  daily__sensortower__revenue: {
    baseValue: 10_000,
    unit: 'days',
  },

  // ========== Monthly 粒度 ==========
  /** MAU: 每月 100万 */
  monthly__sensortower__mau: {
    baseValue: 1_000_000,
    unit: 'months',
  },
  /** Download: 每月 50万 */
  monthly__sensortower__download: {
    baseValue: 500_000,
    unit: 'months',
  },
  /** Revenue: 每月 20万 */
  monthly__sensortower__revenue: {
    baseValue: 200_000,
    unit: 'months',
  },
};

/**
 * 默认阈值结果（用于配置不存在或日期无效时）
 */
export const DEFAULT_THRESHOLD: IThresholdResult = {
  min: null,
  max: null,
};

/**
 * 操作符映射
 */
export const THRESHOLD_OPERATOR_MAP: Record<keyof IThresholdResult, '>=' | '<'> = {
  min: '>=',
  max: '<',
} as const;
